import { defineComponent, inject, computed, ref, watch, createBlock, openBlock, withCtx, createElementVNode, createElementBlock, createCommentVNode, createTextVNode, unref, toDisplayString, normalizeClass, Fragment, renderList, createVNode, withDirectives } from "vue";
import { useI18n } from "vue-i18n";
import { I18n } from "app-core";
import moment from "moment-timezone";
import { Icon } from "@iconify/vue";
import { maskito } from "@maskito/vue";
import UFormField from "@nuxt/ui/components/FormField.vue";
import UInput from "@nuxt/ui/components/Input.vue";
import UCheckbox from "@nuxt/ui/components/Checkbox.vue";
import UButton from "@nuxt/ui/components/Button.vue";
import UFileUpload from "@nuxt/ui/components/FileUpload.vue";
import CodSheet from "./index28.js";
import _sfc_main$1 from "./index29.js";
import { encodeFiles } from "./index52.js";
import { useMaskito } from "./index51.js";
const _hoisted_1 = { class: "flex min-h-0 flex-1 flex-col" };
const _hoisted_2 = {
  key: 0,
  class: "flex shrink-0 items-center justify-center gap-3 px-4 pb-3 pt-6 text-panel-title"
};
const _hoisted_3 = {
  key: 1,
  class: "min-h-0 flex-1 overflow-y-auto p-4 leading-snug"
};
const _hoisted_4 = ["innerHTML"];
const _hoisted_5 = {
  key: 1,
  class: "space-y-4"
};
const _hoisted_6 = {
  role: "radiogroup",
  class: "overflow-hidden rounded-card border border-default"
};
const _hoisted_7 = ["aria-checked", "onClick"];
const _hoisted_8 = { class: "min-w-0 flex-1 truncate" };
const _hoisted_9 = { class: "flex shrink-0 gap-2 p-3" };
const _hoisted_10 = {
  key: 1,
  class: "flex-1"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "CodConfirm",
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    config: {
      type: Object,
      required: true
    }
  },
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {
    const { t } = useI18n();
    const emit = __emit;
    const { createDateMask } = useMaskito();
    const actionSize = inject("kontyBackoffice", false) ? "lg" : "lg-plus";
    const dateFormat = I18n.c("general.formatDate");
    const dateMask = createDateMask({ dateFormat });
    const defaultConfig = {
      title: t("general.are_you_sure"),
      message: "",
      buttonCancelText: t("general.cancel"),
      buttonTrueText: t("general.confirm"),
      buttonFalseText: "",
      buttonCancelColor: "neutral",
      buttonTrueColor: "primary",
      // The "no" branch is a secondary action — neutral outline per the recipe
      // catalog (callsites can still pass e.g. 'error' for destructive splits).
      buttonFalseColor: "neutral",
      icon: "i-lucide-circle-alert",
      canDismiss: true,
      focusSheetOnOpen: false,
      expand: false,
      width: "420px",
      inputs: [],
      yesHandler: () => {
      },
      noHandler: () => {
      },
      cancelHandler: () => {
      }
    };
    const props = __props;
    const open = computed({
      get: () => props.modelValue,
      set: (v) => emit("update:modelValue", v)
    });
    const resolved = ref(true);
    const configInt = ref(defaultConfig);
    const canDismiss = ref(true);
    const values = ref({});
    const fileValues = ref({});
    watch(() => props.modelValue, (isOpen) => {
      var _a, _b, _c;
      if (!isOpen) {
        if (!resolved.value) {
          resolved.value = true;
          (_b = (_a = props.config) == null ? void 0 : _a.cancelHandler) == null ? void 0 : _b.call(_a, values.value);
        }
        return;
      }
      resolved.value = false;
      configInt.value = { ...defaultConfig, ...props.config };
      canDismiss.value = configInt.value.canDismiss === void 0 || configInt.value.canDismiss;
      values.value = {};
      fileValues.value = {};
      (_c = configInt.value.inputs) == null ? void 0 : _c.forEach((input) => {
        values.value[input.key] = input.default || void 0;
      });
    }, { immediate: true });
    const disableButtonTrue = computed(() => {
      var _a;
      if (!((_a = configInt.value.inputs) == null ? void 0 : _a.length)) return false;
      let disabled = false;
      configInt.value.inputs.forEach((input) => {
        var _a2;
        const validations = ((_a2 = input.validation) == null ? void 0 : _a2.split("|")) || [];
        for (const validation of validations) {
          if (validation === "required" && !values.value[input.key]) {
            disabled = true;
          }
          if (validation === "filled" && !String(values.value[input.key] ?? "").trim()) {
            disabled = true;
          }
          if ((validation == null ? void 0 : validation.startsWith("max:")) && String(values.value[input.key] ?? "").trim().length > Number(validation.slice(4))) {
            disabled = true;
          }
          if (validation == null ? void 0 : validation.startsWith("required_if")) {
            const segments = validation.split(":");
            if (segments.length > 1) {
              const key = segments[1].split(",")[0];
              const value = segments[1].split(",")[1];
              if (!values.value[input.key] && values.value[key] === value) {
                disabled = true;
              }
            }
          }
          if (validation == null ? void 0 : validation.startsWith("required_without_all")) {
            const segments = validation.split(":");
            const withoutFields = segments.length > 1 ? segments[1].split(",").filter((key) => {
              var _a3;
              return (_a3 = configInt.value.inputs) == null ? void 0 : _a3.some((f) => f.key === key);
            }) : (configInt.value.inputs || []).filter((f) => f.key !== input.key).map((f) => f.key);
            const noWithoutFieldsHaveValue = !withoutFields.some((key) => !!values.value[key]);
            if (noWithoutFieldsHaveValue && !values.value[input.key]) {
              disabled = true;
            }
          }
          if ((validation == null ? void 0 : validation.startsWith("date")) && values.value[input.key]) {
            if (!moment(values.value[input.key], dateFormat).isValid()) {
              disabled = true;
            }
          }
        }
      });
      return disabled;
    });
    function choose(result) {
      var _a, _b, _c, _d;
      if (result === true) {
        resolved.value = true;
        (_b = (_a = props.config).yesHandler) == null ? void 0 : _b.call(_a, values.value);
      } else if (result === false) {
        resolved.value = true;
        (_d = (_c = props.config).noHandler) == null ? void 0 : _d.call(_c, values.value);
      }
      emit("update:modelValue", false);
    }
    function iconifyName(icon) {
      if (!icon) return "";
      if (icon.includes(":")) return icon;
      const match = icon.match(/^i-(lucide|ph)-(.+)$/);
      return match ? `${match[1]}:${match[2]}` : icon;
    }
    function inputFilter(input) {
      if (!input.filter && !input.searchBy) return void 0;
      return (item, query) => filter(item, query, input);
    }
    function filter(item, query, input) {
      if (input.filter) return input.filter(item, query);
      return (input.searchBy ?? []).some((field) => {
        var _a;
        return ((_a = item[field]) == null ? void 0 : _a.toLowerCase().indexOf(query.toLowerCase().trim())) > -1;
      });
    }
    function onChange(input) {
      var _a;
      (_a = input.onChange) == null ? void 0 : _a.call(input, values.value);
    }
    function radioValue(input, item) {
      return input.itemKey ? item[input.itemKey] : item;
    }
    function radioLabel(input, item) {
      return input.itemText ? String(item[input.itemText] ?? "") : String(item);
    }
    function isRadioSelected(input, item) {
      return values.value[input.key] === radioValue(input, item);
    }
    function selectRadio(input, item) {
      values.value[input.key] = radioValue(input, item);
      onChange(input);
    }
    async function onFileChange(files, input) {
      var _a;
      fileValues.value[input.key] = files;
      values.value[input.key] = await encodeFiles(files);
      (_a = input.onChange) == null ? void 0 : _a.call(input, values.value);
    }
    return (_ctx, _cache) => {
      return openBlock(), createBlock(CodSheet, {
        modelValue: open.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => open.value = $event),
        guard: !canDismiss.value,
        width: configInt.value.width,
        height: configInt.value.expand ? "85dvh" : "auto",
        "fullscreen-mobile": !!configInt.value.expand,
        "focus-sheet-on-open": !!configInt.value.focusSheetOnOpen,
        elevated: ""
      }, {
        default: withCtx(() => {
          var _a, _b, _c;
          return [
            createElementVNode("div", _hoisted_1, [
              configInt.value.title || configInt.value.icon ? (openBlock(), createElementBlock("div", _hoisted_2, [
                configInt.value.icon ? (openBlock(), createBlock(unref(Icon), {
                  key: 0,
                  icon: iconifyName(configInt.value.icon),
                  class: "size-6 shrink-0"
                }, null, 8, ["icon"])) : createCommentVNode("", true),
                createTextVNode(" " + toDisplayString(configInt.value.title), 1)
              ])) : createCommentVNode("", true),
              configInt.value.message || ((_a = configInt.value.inputs) == null ? void 0 : _a.length) ? (openBlock(), createElementBlock("div", _hoisted_3, [
                configInt.value.message ? (openBlock(), createElementBlock("div", {
                  key: 0,
                  innerHTML: configInt.value.message,
                  class: normalizeClass(["flex justify-center", { "mb-5": (_b = configInt.value.inputs) == null ? void 0 : _b.length }])
                }, null, 10, _hoisted_4)) : createCommentVNode("", true),
                ((_c = configInt.value.inputs) == null ? void 0 : _c.length) ? (openBlock(), createElementBlock("div", _hoisted_5, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(configInt.value.inputs, (input, index) => {
                    return openBlock(), createElementBlock(Fragment, {
                      key: "cconf" + index
                    }, [
                      input.type === "select" ? (openBlock(), createBlock(UFormField, {
                        key: 0,
                        label: input.label,
                        help: input.hint
                      }, {
                        default: withCtx(() => [
                          createVNode(_sfc_main$1, {
                            modelValue: values.value[input.key],
                            "onUpdate:modelValue": [($event) => values.value[input.key] = $event, ($event) => onChange(input)],
                            items: input.items,
                            "item-text": input.itemText,
                            "item-key": input.itemKey,
                            "return-object": !input.itemKey,
                            filter: inputFilter(input)
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "items", "item-text", "item-key", "return-object", "filter"])
                        ]),
                        _: 2
                      }, 1032, ["label", "help"])) : createCommentVNode("", true),
                      input.type === "text" || input.type === "password" ? (openBlock(), createBlock(UFormField, {
                        key: 1,
                        label: input.label,
                        help: input.hint
                      }, {
                        default: withCtx(() => [
                          createVNode(UInput, {
                            modelValue: values.value[input.key],
                            "onUpdate:modelValue": [($event) => values.value[input.key] = $event, ($event) => onChange(input)],
                            class: "w-full",
                            type: input.type
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "type"])
                        ]),
                        _: 2
                      }, 1032, ["label", "help"])) : createCommentVNode("", true),
                      input.type === "date" ? (openBlock(), createBlock(UFormField, {
                        key: 2,
                        label: input.label,
                        help: input.hint
                      }, {
                        default: withCtx(() => [
                          withDirectives(createVNode(UInput, {
                            modelValue: values.value[input.key],
                            "onUpdate:modelValue": [($event) => values.value[input.key] = $event, ($event) => onChange(input)],
                            inputmode: "numeric",
                            class: "w-full",
                            placeholder: unref(dateFormat)
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "placeholder"]), [
                            [unref(maskito), unref(dateMask)]
                          ])
                        ]),
                        _: 2
                      }, 1032, ["label", "help"])) : createCommentVNode("", true),
                      input.type === "file" ? (openBlock(), createBlock(UFileUpload, {
                        key: 3,
                        layout: "list",
                        multiple: "",
                        "model-value": fileValues.value[input.key] ?? null,
                        label: input.label,
                        description: input.hint,
                        "onUpdate:modelValue": (files) => onFileChange(files ?? [], input)
                      }, null, 8, ["model-value", "label", "description", "onUpdate:modelValue"])) : createCommentVNode("", true),
                      input.type === "checkbox" ? (openBlock(), createBlock(UCheckbox, {
                        key: 4,
                        "model-value": !!values.value[input.key],
                        label: input.label,
                        description: input.hint,
                        "onUpdate:modelValue": (v) => {
                          values.value[input.key] = v === true;
                          onChange(input);
                        }
                      }, null, 8, ["model-value", "label", "description", "onUpdate:modelValue"])) : createCommentVNode("", true),
                      input.type === "radio" ? (openBlock(), createBlock(UFormField, {
                        key: 5,
                        label: input.label || void 0,
                        help: input.hint
                      }, {
                        default: withCtx(() => [
                          createElementVNode("div", _hoisted_6, [
                            (openBlock(true), createElementBlock(Fragment, null, renderList(input.items, (item, itemIndex) => {
                              return openBlock(), createElementBlock("button", {
                                key: "cconfr" + index + "-" + itemIndex,
                                type: "button",
                                role: "radio",
                                "data-test": "radio-row",
                                "aria-checked": isRadioSelected(input, item),
                                class: normalizeClass(["flex min-h-12 w-full items-center gap-3 px-3.5 text-left", [
                                  { "border-t border-default": itemIndex > 0 },
                                  isRadioSelected(input, item) ? "bg-(--app-tint-accent) font-semibold text-highlighted" : "hover:bg-elevated active:bg-elevated"
                                ]]),
                                onClick: ($event) => selectRadio(input, item)
                              }, [
                                createElementVNode("span", _hoisted_8, toDisplayString(radioLabel(input, item)), 1),
                                isRadioSelected(input, item) ? (openBlock(), createBlock(unref(Icon), {
                                  key: 0,
                                  icon: "lucide:check",
                                  class: "size-5 shrink-0 text-(--app-text-accent)"
                                })) : createCommentVNode("", true)
                              ], 10, _hoisted_7);
                            }), 128))
                          ])
                        ]),
                        _: 2
                      }, 1032, ["label", "help"])) : createCommentVNode("", true)
                    ], 64);
                  }), 128))
                ])) : createCommentVNode("", true)
              ])) : createCommentVNode("", true),
              createElementVNode("div", _hoisted_9, [
                configInt.value.buttonCancelText ? (openBlock(), createBlock(UButton, {
                  key: 0,
                  class: "flex-1",
                  size: unref(actionSize),
                  color: configInt.value.buttonCancelColor,
                  variant: configInt.value.buttonFalseText ? "ghost" : "outline",
                  onClick: _cache[0] || (_cache[0] = ($event) => choose(void 0))
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(configInt.value.buttonCancelText), 1)
                  ]),
                  _: 1
                }, 8, ["size", "color", "variant"])) : createCommentVNode("", true),
                configInt.value.buttonCancelText && configInt.value.buttonFalseText ? (openBlock(), createElementBlock("div", _hoisted_10)) : createCommentVNode("", true),
                configInt.value.buttonFalseText ? (openBlock(), createBlock(UButton, {
                  key: 2,
                  class: "flex-1",
                  size: unref(actionSize),
                  color: configInt.value.buttonFalseColor,
                  variant: "outline",
                  onClick: _cache[1] || (_cache[1] = ($event) => choose(false))
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(configInt.value.buttonFalseText), 1)
                  ]),
                  _: 1
                }, 8, ["size", "color"])) : createCommentVNode("", true),
                configInt.value.buttonTrueText ? (openBlock(), createBlock(UButton, {
                  key: 3,
                  class: "flex-1",
                  size: unref(actionSize),
                  color: configInt.value.buttonTrueColor,
                  variant: "solid",
                  disabled: disableButtonTrue.value,
                  onClick: _cache[2] || (_cache[2] = ($event) => choose(true))
                }, {
                  default: withCtx(() => [
                    createTextVNode(toDisplayString(configInt.value.buttonTrueText), 1)
                  ]),
                  _: 1
                }, 8, ["size", "color", "disabled"])) : createCommentVNode("", true)
              ])
            ])
          ];
        }),
        _: 1
      }, 8, ["modelValue", "guard", "width", "height", "fullscreen-mobile", "focus-sheet-on-open"]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index32.js.map
